import type { BeckyFunctionLibrary, BeckyRequestPayload, BeckyModel } from '@wix/thunderbolt-becky-root'
import { ModuleFetcher, Logger, ProcessOoiCssFunctionLib, TPAWidgetDefinition } from '@wix/thunderbolt-becky-types'
import { fetchModules } from './moduleResolverFactory'
import { decodeModulesToLoad, processOoiCss, ooiStyleParamsUtil, isOOI } from '@wix/thunderbolt-commons'
import _ from 'lodash'

const isPublicSegment = process.env.PACKAGE_NAME !== 'thunderbolt-ds'

const resolveWidgetsToLoad = (ooiVersions: BeckyRequestPayload['ooiVersions'], widgetIdsOnPage: Array<string> = []) => {
	const allWidgetsOnSite = decodeModulesToLoad(ooiVersions || '')
	return allWidgetsOnSite.filter((w) => widgetIdsOnPage.find((widgetId) => widgetId === w.id))
}

const staticCssProcessorMemoizer = (getBuildTimeStaticCss: (cssConfig: object) => { css: string; hash?: string }) => {
	const staticCssHashes: { [hash: string]: boolean } = {}
	return (cssConfig: object) => {
		const { css, hash } = getBuildTimeStaticCss(cssConfig)
		if (!hash) {
			// backward-compatibility: cssConfig might not include the new build-time hashing feature (in tpa-webpack-plugin)
			return css
		}
		if (staticCssHashes[hash]) {
			// since staticCss already exists, we don't to return it
			return ''
		}
		staticCssHashes[hash] = true
		return css
	}
}

function processOoiCssFactory({
	runAndReport,
	widgetsWithAppSettings,
	isResponsive,
	anywhereThemeOverride,
}: {
	runAndReport: BeckyFunctionLibrary['runAndReport']
	widgetsWithAppSettings: Record<string, any> | undefined
	isResponsive: boolean
	anywhereThemeOverride?: string
}): ProcessOoiCssFunctionLib['processOoiCss'] {
	// can't use typescript import due to jest transfer exploding when looking into tpa-style-webpack-plugin (and es6 module)
	const { getBuildTimeStaticCss } = require('@wix/tpa-style-webpack-plugin/standalone')
	const staticCssProcessor = staticCssProcessorMemoizer(getBuildTimeStaticCss)
	return (
		widgetId: string,
		styleData: any,
		{ prefixSelector, isRTL, isMobile, usesCssPerBreakpoint, dimensions, cssConfig },
		referenceId: string | undefined,
		tpaData: any
	): string => {
		let { style: styleParams } = styleData

		if (ooiStyleParamsUtil.shouldOverrideStyleParams(widgetId)) {
			const appSettings = widgetsWithAppSettings?.[`${widgetId}-${referenceId}`]
			styleParams = ooiStyleParamsUtil.overrideStyleParams({
				styleParams,
				widgetId,
				appSettings,
				tpaData,
				isResponsive,
				anywhereThemeOverride,
			})
			styleData.style = styleParams
		}

		if (!cssConfig) {
			return ''
		}

		return processOoiCss({
			widgetId,
			styleData,
			prefixSelector,
			cssConfig,
			isRTL,
			isMobile,
			dimensions,
			usesCssPerBreakpoint,
			getStaticCss: staticCssProcessor,
			shouldRemoveStaticCss: isPublicSegment,
			runAndReport,
			tpaData,
			isResponsive,
		})
	}
}

export const processOoiCssImpl = async ({
	funcLib: { runAndReport },
	beckyModel,
}: {
	funcLib: BeckyFunctionLibrary
	beckyModel: BeckyModel
}) => {
	return processOoiCssFactory({
		runAndReport,
		widgetsWithAppSettings: beckyModel.ooiWidgetsAppSettings,
		isResponsive: beckyModel.isResponsive,
		anywhereThemeOverride: beckyModel.anywhereThemeOverride,
	})
}

export const loadCssConfigs = async (
	ooiVersions: BeckyRequestPayload['ooiVersions'],
	widgetsIds: Array<string>,
	moduleFetcher: ModuleFetcher,
	runAsyncAndReport: any,
	logger: Logger
) => {
	const cssConfigModulesToLoad = resolveWidgetsToLoad(ooiVersions, widgetsIds)
	const loadedModules = await runAsyncAndReport(
		() =>
			fetchModules(
				cssConfigModulesToLoad,
				{
					runAsyncAndReport,
					moduleFetcher,
				},
				logger
			),
		`tb-css-fetch-cssConfig-modules-${Object.keys(cssConfigModulesToLoad).join(',')}`
	)

	return Object.keys(loadedModules).reduce((acc, widgetId) => {
		const module = loadedModules[widgetId]
		if (module?.cssConfig) {
			acc[widgetId] = module.cssConfig
		}
		return acc
	}, {} as BeckyModel['ooiCssConfigStore'])
}

export const getOoiCssModel = (beckyModel: BeckyModel) => {
	const widgetComponents = _(beckyModel.structure)
		.mapValues((component) => {
			const dataQuery = (component.dataQuery || '').replace('#', '')
			const widgetData = beckyModel.data.document_data[dataQuery] as TPAWidgetDefinition['data']
			const appDefinitionId = widgetData?.appDefinitionId
			if (!appDefinitionId) {
				return null
			}
			const widgetSpec = _.find(beckyModel.clientSpecMap, (x) => x.appDefinitionId === appDefinitionId)
				?.widgets?.[widgetData.widgetId]
			if (!widgetSpec) {
				return null
			}
			const isMigratingToOoi = beckyModel.migratingToOoiWidgetIds.includes(appDefinitionId)
			if (
				isOOI({
					widgetSpec,
					isMigratingToOoi,
				})
			) {
				return {
					widgetId: widgetData.widgetId,
					referenceId: widgetData.referenceId,
				}
			}
			return null
		})
		.pickBy((x) => x)
		.value() as Record<string, { widgetId: string; referenceId: string }>
	const widgetsWithReferenceId = _(widgetComponents)
		.pickBy((widget) => widget.referenceId)
		.values()
		.value()
	const widgetsIds = _(widgetComponents)
		.groupBy((widget) => widget.widgetId)
		.keys()
		.value()

	return {
		widgetsWithReferenceId,
		widgetsIds,
	}
}
